import { createContext, useContext, useState, ReactNode, useCallback, useMemo, useEffect } from 'react'
import { 
  WorkflowAssignment, 
  WorkStyle,
  EmployeeWorkload,
  WorkflowStatistics,
  WorkflowFilterOptions,
  CreateWorkflowAssignmentRequest,
  UpdateWorkflowAssignmentRequest,
  WorkflowStatus,
  EmployeeType,
} from '@/types/workflow'
import { useAuth } from '@/contexts/AuthContext'
import { getEmployees } from '@/utils/employees'
import { Employee } from '@/types/employees'
import {
  createWorkflowAssignment,
  getWorkflowAssignments,
  getWorkflowWorkStyles,
  mapDesignationToWorkflowType,
  updateWorkflowAssignment,
  updateWorkflowAssignmentsBatch as persistWorkflowAssignmentsBatch,
  updateWorkflowAssignmentStatus as persistWorkflowAssignmentStatus,
} from '@/utils/workflow'

interface WorkflowContextType {
  assignments: WorkflowAssignment[]
  employeeWorkloads: EmployeeWorkload[]
  workStyles: WorkStyle[]
  loading: boolean
  error: string | null
  statistics: WorkflowStatistics
  fetchAssignments: (filters?: WorkflowFilterOptions) => Promise<void>
  createAssignment: (assignmentData: CreateWorkflowAssignmentRequest) => Promise<WorkflowAssignment>
  updateAssignment: (assignmentData: UpdateWorkflowAssignmentRequest) => Promise<void>
  updateAssignmentStatus: (assignmentId: string, status: WorkflowStatus) => Promise<void>
  updateAssignmentsBatch: (workflowIds: string[], updates: { employeeId?: string; notes?: string }) => Promise<void>
  updateAssignmentStatusByWorkflowIds: (workflowIds: string[], status: WorkflowStatus) => Promise<void>
  getAssignmentById: (id: string) => WorkflowAssignment | undefined
  getAssignmentsByOrder: (orderId: string) => WorkflowAssignment[]
  getAssignmentsByEmployee: (employeeId: string) => WorkflowAssignment[]
  getEmployeeWorkload: (employeeId: string) => EmployeeWorkload | undefined
  getAvailableEmployees: (employeeType: EmployeeType) => EmployeeWorkload[]
  getAvailableWorkStyles: (garmentType: string) => WorkStyle[]
}

const WorkflowContext = createContext<WorkflowContextType | undefined>(undefined)

interface WorkflowProviderProps {
  children: ReactNode
}

export function WorkflowProvider({ children }: WorkflowProviderProps) {
  const { isAuthenticated } = useAuth()
  const [assignments, setAssignments] = useState<WorkflowAssignment[]>([])
  const [employees, setEmployees] = useState<Employee[]>([])
  const [workStyles, setWorkStyles] = useState<WorkStyle[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const employeeWorkloads = useMemo<EmployeeWorkload[]>(() => {
    return employees
      .map((employee) => {
        const employeeType = mapDesignationToWorkflowType(employee.designation)
        if (!employeeType) {
          return null
        }

        const employeeAssignments = assignments.filter(
          (assignment) => assignment.employeeId === employee.id
        )
        const activeAssignments = employeeAssignments.filter(
          (assignment) => assignment.status !== WorkflowStatus.COMPLETED
        )
        const completedAssignments = employeeAssignments.filter(
          (assignment) => assignment.status === WorkflowStatus.COMPLETED
        )

        return {
          employeeId: employee.id,
          employeeName: employee.fullName,
          employeeType,
          currentJobs: activeAssignments.length,
          totalAssigned: employeeAssignments.length,
          completedJobs: completedAssignments.length,
          totalEarnings: employeeAssignments.reduce((sum, assignment) => sum + assignment.totalPayment, 0),
          activeAssignments
        }
      })
      .filter((workload): workload is EmployeeWorkload => Boolean(workload))
  }, [assignments, employees])

  const statistics = useMemo<WorkflowStatistics>(() => {
    const now = new Date()
    const currentMonth = now.getMonth()
    const currentYear = now.getFullYear()

    const isCurrentMonth = (date?: Date) =>
      Boolean(date) &&
      date!.getMonth() === currentMonth &&
      date!.getFullYear() === currentYear

    const monthlyAssignments = assignments.filter((assignment) =>
      isCurrentMonth(assignment.assignedAt)
    ).length

    const pendingAssignments = assignments.filter(a => a.status === WorkflowStatus.PENDING).length
    const monthlyCompletedJobs = assignments.filter(
      (assignment) => assignment.status === WorkflowStatus.COMPLETED && isCurrentMonth(assignment.completedAt)
    ).length
    const assignmentsUnderProcess = assignments.filter(
      (assignment) => assignment.status !== WorkflowStatus.COMPLETED
    ).length
    const totalPayments = assignments.reduce((sum, a) => sum + a.totalPayment, 0)

    return {
      monthlyAssignments,
      pendingAssignments,
      monthlyCompletedJobs,
      assignmentsUnderProcess,
      totalPayments
    }
  }, [assignments])

  const fetchAssignments = useCallback(async (filters?: WorkflowFilterOptions) => {
    if (!isAuthenticated) {
      return
    }

    setLoading(true)
    setError(null)
    try {
      const workflowAssignments = await getWorkflowAssignments(filters)
      setAssignments(workflowAssignments)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch assignments')
    } finally {
      setLoading(false)
    }
  }, [isAuthenticated])

  const fetchEmployees = useCallback(async () => {
    if (!isAuthenticated) {
      return
    }

    try {
      const data = await getEmployees()
      setEmployees(data.filter((employee) => employee.status === 'ACTIVE'))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch employees')
    }
  }, [isAuthenticated])

  const fetchWorkStyles = useCallback(async () => {
    if (!isAuthenticated) {
      return
    }

    try {
      const data = await getWorkflowWorkStyles()
      setWorkStyles(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch workflow work styles')
    }
  }, [isAuthenticated])

  const createAssignment = useCallback(async (assignmentData: CreateWorkflowAssignmentRequest): Promise<WorkflowAssignment> => {
    setLoading(true)
    setError(null)
    try {
      const newAssignment = await createWorkflowAssignment(assignmentData)
      await fetchAssignments()
      return newAssignment
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create assignment')
      throw err
    } finally {
      setLoading(false)
    }
  }, [fetchAssignments])

  const updateAssignment = useCallback(async (assignmentData: UpdateWorkflowAssignmentRequest) => {
    setLoading(true)
    setError(null)
    try {
      await updateWorkflowAssignment(assignmentData)
      await fetchAssignments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update assignment')
      throw err
    } finally {
      setLoading(false)
    }
  }, [fetchAssignments])

  const updateAssignmentStatus = useCallback(async (assignmentId: string, status: WorkflowStatus) => {
    setLoading(true)
    setError(null)
    try {
      const assignment = assignments.find(a => a.id === assignmentId)
      if (!assignment) {
        throw new Error('Assignment not found')
      }

      await persistWorkflowAssignmentStatus(assignment.workflowIds, status)
      await fetchAssignments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update assignment status')
      throw err
    } finally {
      setLoading(false)
    }
  }, [assignments, fetchAssignments])

  const updateAssignmentsBatch = useCallback(async (
    workflowIds: string[],
    updates: { employeeId?: string; notes?: string }
  ) => {
    setLoading(true)
    setError(null)
    try {
      await persistWorkflowAssignmentsBatch(workflowIds, updates)
      await fetchAssignments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update workflow assignments')
      throw err
    } finally {
      setLoading(false)
    }
  }, [fetchAssignments])

  const updateAssignmentStatusByWorkflowIds = useCallback(async (workflowIds: string[], status: WorkflowStatus) => {
    setLoading(true)
    setError(null)
    try {
      await persistWorkflowAssignmentStatus(workflowIds, status)
      await fetchAssignments()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update workflow assignment status')
      throw err
    } finally {
      setLoading(false)
    }
  }, [fetchAssignments])

  const getAssignmentById = useCallback((id: string): WorkflowAssignment | undefined => {
    return assignments.find(assignment => assignment.id === id)
  }, [assignments])

  const getAssignmentsByOrder = useCallback((orderId: string): WorkflowAssignment[] => {
    return assignments.filter(assignment => assignment.orderId === orderId)
  }, [assignments])

  const getAssignmentsByEmployee = useCallback((employeeId: string): WorkflowAssignment[] => {
    return assignments.filter(assignment => assignment.employeeId === employeeId)
  }, [assignments])

  const getEmployeeWorkload = useCallback((employeeId: string): EmployeeWorkload | undefined => {
    return employeeWorkloads.find(emp => emp.employeeId === employeeId)
  }, [employeeWorkloads])

  const getAvailableEmployees = useCallback((employeeType: EmployeeType): EmployeeWorkload[] => {
    return employeeWorkloads.filter(emp => emp.employeeType === employeeType)
  }, [employeeWorkloads])

  const getAvailableWorkStyles = useCallback((garmentType: string): WorkStyle[] => {
    const employeeType = garmentType === 'Jacket & Vest' || garmentType === 'Sherwani'
      ? EmployeeType.JACKET_MAKER
      : garmentType === 'Alteration'
        ? EmployeeType.ALTERATION
      : garmentType === 'Trouser'
        ? EmployeeType.TROUSER_MAKER
        : EmployeeType.SHIRT_MAKER

    return workStyles.filter((style) => {
      if (employeeType === EmployeeType.JACKET_MAKER || employeeType === EmployeeType.ALTERATION) {
        return style.category === 'Jackets'
      }

      if (employeeType === EmployeeType.TROUSER_MAKER) {
        return style.category === 'Trousers'
      }

      return style.category === 'Shirts'
    }).sort((a, b) => {
      if (b.rate !== a.rate) {
        return b.rate - a.rate
      }

      return a.name.localeCompare(b.name)
    })
  }, [workStyles])

  useEffect(() => {
    if (isAuthenticated) {
      fetchEmployees()
      fetchWorkStyles()
    } else {
      setAssignments([])
      setEmployees([])
      setWorkStyles([])
      setError(null)
    }
  }, [fetchEmployees, fetchWorkStyles, isAuthenticated])

  const value: WorkflowContextType = {
    assignments,
    employeeWorkloads,
    workStyles,
    loading,
    error,
    statistics,
    fetchAssignments,
    createAssignment,
    updateAssignment,
    updateAssignmentStatus,
    updateAssignmentsBatch,
    updateAssignmentStatusByWorkflowIds,
    getAssignmentById,
    getAssignmentsByOrder,
    getAssignmentsByEmployee,
    getEmployeeWorkload,
    getAvailableEmployees,
    getAvailableWorkStyles
  }

  return (
    <WorkflowContext.Provider value={value}>
      {children}
    </WorkflowContext.Provider>
  )
}

export function useWorkflow() {
  const context = useContext(WorkflowContext)
  if (context === undefined) {
    throw new Error('useWorkflow must be used within a WorkflowProvider')
  }
  return context
}
